import { Content } from "@patternfly/react-core";
import React, { ComponentType, ReactNode, Suspense } from "react";
import { Navigate, RouteObject } from "react-router-dom";
import App from "./App";
import NotFound from "./app/components/NotFound";
import AuthGuard from "./app/components/guards/AuthGuard";
import ExternalAuthGuard from "./app/components/guards/ExternalAuthGuard";
import InternalAuthGuard from "./app/components/guards/InternalAuthGuard";

// Sure would be nice if I could use https://github.com/tc39/proposal-pipeline-operator
const LazyDefaultInternalRoute = withInternalAuthGuard(
  withSuspense(() => import("./app/routes/DefaultInternalRoute")),
);
const LazyTestingLocations = withSuspense(
  () => import("./app/routes/TestingLocations"),
);
const LazyAllExamDashboard = withExternalAuthGuard(
  withSuspense(() => import("./app/routes/dashboard/AllExamDashboard")),
);
const LazyEligibilityDetails = withExternalAuthGuard(
  withSuspense(() => import("./app/routes/dashboard/ExamDetails")),
);
const LazyProfile = withAuthGuard(
  withSuspense(() => import("./app/routes/settings/SmartProfile")),
);
const LazyExamDelivery = withInternalAuthGuard(
  withSuspense(() => import("./app/routes/exam-deliveries")),
);
const LazyExamDeliveryDetails = withInternalAuthGuard(
  withSuspense(() => import("./app/routes/exam-deliveries/details")),
);
const LazyLogOut = withSuspense(() => import("./app/routes/LogOut"));
const LazyManageCountries = withInternalAuthGuard(
  withSuspense(() => import("./app/routes/manage-countries")),
);
const LazyUpdateCountry = withInternalAuthGuard(
  withSuspense(() => import("./app/routes/manage-countries/UpdateCountry")),
);
const LazyCreateCountry = withInternalAuthGuard(
  withSuspense(() => import("./app/routes/manage-countries/CreateCountry")),
);
const LazyManageGlobalAdministrators = withInternalAuthGuard(
  withSuspense(() => import("./app/routes/manage-global-administrators")),
);
const LazyEditGlobalAdministrator = withInternalAuthGuard(
  withSuspense(
    () =>
      import(
        "./app/routes/manage-global-administrators/EditGlobalAdministrator"
      ),
  ),
);
const LazyAddGlobalAdministrator = withInternalAuthGuard(
  withSuspense(
    () =>
      import(
        "./app/routes/manage-global-administrators/AddGlobalAdministrator"
      ),
  ),
);
const LazyManageSiteAdministrators = withInternalAuthGuard(
  withSuspense(() => import("./app/routes/manage-site-administrators")),
);
const LazyEditSiteAdministrator = withInternalAuthGuard(
  withSuspense(
    () =>
      import("./app/routes/manage-site-administrators/EditSiteAdministrator"),
  ),
);
const LazyAddSiteAdministrator = withInternalAuthGuard(
  withSuspense(
    () =>
      import("./app/routes/manage-site-administrators/AddSiteAdministrator"),
  ),
);
const LazyManageRegionalAdministrators = withInternalAuthGuard(
  withSuspense(() => import("./app/routes/manage-regional-administrators")),
);
const LazyEditRegionalAdministrator = withInternalAuthGuard(
  withSuspense(
    () =>
      import(
        "./app/routes/manage-regional-administrators/EditRegionalAdministrator"
      ),
  ),
);
const LazyAddRegionalAdministrator = withInternalAuthGuard(
  withSuspense(
    () =>
      import(
        "./app/routes/manage-regional-administrators/AddRegionalAdministrator"
      ),
  ),
);
const LazyManageTestingCenters = withInternalAuthGuard(
  withSuspense(() => import("./app/routes/manage-testing-centers")),
);
const LazyEditTestingCenter = withInternalAuthGuard(
  withSuspense(
    () => import("./app/routes/manage-testing-centers/EditTestingCenter"),
  ),
);
const LazyConfigureKoalas = withInternalAuthGuard(
  withSuspense(
    () => import("./app/routes/manage-testing-centers/ConfigureKoalas"),
  ),
);
const LazyEligibilityMessages = withInternalAuthGuard(
  withSuspense(() => import("./app/routes/eligibility-messages")),
);
const LazyGovernmentIDApproval = withInternalAuthGuard(
  withSuspense(() => import("./app/routes/government-id-approval")),
);
const LazyGovernmentIDDetails = withInternalAuthGuard(
  withSuspense(
    () => import("./app/routes/government-id-approval/GovernmentIDDetails"),
  ),
);
const LazyManageProctorAlert = withInternalAuthGuard(
  withSuspense(() => import("./app/routes/manage-proctor-alert")),
);
const LazyManageChatIdleTime = withInternalAuthGuard(
  withSuspense(() => import("./app/routes/manage-chat-idle-time")),
);
const LazyUniversalHoursOfOperation = withInternalAuthGuard(
  withSuspense(() => import("./app/routes/universal-hours-of-operation")),
);
const LazyConcurrentExams = withInternalAuthGuard(
  withSuspense(() => import("./app/routes/concurrent-exams")),
);
const LazyManageExams = withInternalAuthGuard(
  withSuspense(() => import("./app/routes/manage-exams")),
);
const LazyEditExam = withInternalAuthGuard(
  withSuspense(() => import("./app/routes/manage-exams/edit-exam")),
);
const LazyCreateExam = withInternalAuthGuard(
  withSuspense(() => import("./app/routes/manage-exams/CreateExam")),
);

const routes: RouteObject[] = [
  {
    path: import.meta.env.BASE_URL,
    element: <App />,
    children: [
      {
        path: "",
        element: <Navigate to="external" />,
      },
      {
        path: "external",
        element: <Navigate to="dashboard" />,
      },
      {
        path: "external/public",
        element: <Navigate to="testing-locations" />,
      },
      {
        path: "external/public/testing-locations",
        element: <LazyTestingLocations />,
      },
      {
        path: "external/dashboard",
        element: <LazyAllExamDashboard />,
      },
      {
        path: "external/dashboard/:code",
        loader: ({ params }) => {
          return Promise.resolve(params.code);
        },
        element: <LazyEligibilityDetails />,
      },
      {
        path: "internal",
        element: <LazyDefaultInternalRoute />,
      },
      {
        path: "internal/exam-deliveries",
        element: <LazyExamDelivery />,
      },
      {
        path: "internal/exam-deliveries/:code",
        loader: ({ params: { code } }) => ({ code }),
        element: <LazyExamDeliveryDetails />,
      },
      {
        path: "internal/manage-countries",
        element: <LazyManageCountries />,
      },
      {
        path: "internal/manage-countries/:id",
        loader: ({ params: { id } }) => Promise.resolve(parseInt(id ?? "", 10)),
        element: <LazyUpdateCountry />,
      },
      {
        path: "internal/manage-countries/create",
        element: <LazyCreateCountry />,
      },
      {
        path: "internal/manage-global-administrators",
        element: <LazyManageGlobalAdministrators />,
      },
      {
        path: "internal/manage-global-administrators/add",
        element: <LazyAddGlobalAdministrator />,
      },
      {
        path: "internal/manage-global-administrators/:id",
        loader: ({ params: { id } }) => Promise.resolve(id),
        element: <LazyEditGlobalAdministrator />,
      },
      {
        path: "internal/manage-site-administrators",
        element: <LazyManageSiteAdministrators />,
      },
      {
        path: "internal/manage-site-administrators/add",
        element: <LazyAddSiteAdministrator />,
      },
      {
        path: "internal/manage-site-administrators/:id",
        loader: ({ params: { id } }) => Promise.resolve(id),
        element: <LazyEditSiteAdministrator />,
      },
      {
        path: "internal/manage-regional-administrators",
        element: <LazyManageRegionalAdministrators />,
      },
      {
        path: "internal/manage-regional-administrators/add",
        element: <LazyAddRegionalAdministrator />,
      },
      {
        path: "internal/manage-regional-administrators/:id",
        loader: ({ params: { id } }) => Promise.resolve(id),
        element: <LazyEditRegionalAdministrator />,
      },
      {
        path: "internal/manage-testing-centers",
        element: <LazyManageTestingCenters />,
      },
      {
        path: "internal/manage-testing-centers/:id",
        loader: ({ params: { id } }) => parseInt(id ?? "0", 10),
        element: <LazyEditTestingCenter />,
      },
      {
        path: "internal/manage-testing-centers/:id/koalas",
        loader: ({ params: { id } }) => parseInt(id ?? "0", 10),
        element: <LazyConfigureKoalas />,
      },
      {
        path: "internal/manage-exams",
        element: <LazyManageExams />,
      },
      {
        path: "internal/manage-exams/:id",
        loader: ({ params: { id } }) => parseInt(id ?? "0", 10),
        element: <LazyEditExam />,
      },
      {
        path: "internal/manage-exams/create",
        element: <LazyCreateExam />,
      },
      {
        path: "internal/eligibility-messages",
        element: <LazyEligibilityMessages />,
      },
      {
        path: "internal/government-id-approval",
        element: <LazyGovernmentIDApproval />,
      },
      {
        path: "internal/government-id-approval/:id",
        loader: ({ params: { id } }) => parseInt(id ?? "0", 10),
        element: <LazyGovernmentIDDetails />,
      },
      {
        path: "internal/manage-proctor-alert",
        element: <LazyManageProctorAlert />,
      },
      {
        path: "internal/universal-hours-of-operation",
        element: <LazyUniversalHoursOfOperation />,
      },
      {
        path: "internal/concurrent-exams",
        element: <LazyConcurrentExams />,
      },
      {
        path: "internal/manage-chat-idle-time",
        element: <LazyManageChatIdleTime />,
      },
      {
        path: "settings/profile",
        element: <LazyProfile />,
      },
      {
        path: "log-out",
        element: <LazyLogOut />,
      },
      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
];

export default routes;

/**
 * This higher-order component takes a component and wraps it with an AuthGuard
 *
 * @param {ComponentType<P>} WrappedComponent
 * @returns {ComponentType<P>} WrappedComponentWithAuthGuard
 */
function withAuthGuard<P extends object>(
  WrappedComponent: ComponentType<P>,
): ComponentType<P> {
  return function WrappedComponentWithAuthGuard(props: P) {
    return (
      <AuthGuard>
        <WrappedComponent {...props} />
      </AuthGuard>
    );
  };
}

/**
 * This higher-order component takes a component and wraps it with an ExternalAuthGuard
 *
 * @param {ComponentType<P>} WrappedComponent
 * @returns {ComponentType<P>} WrappedComponentWithExternalAuthGuard
 */
function withExternalAuthGuard<P extends object>(
  WrappedComponent: ComponentType<P>,
): ComponentType<P> {
  return function WrappedComponentWithExternalAuthGuard(props: P) {
    return (
      <ExternalAuthGuard>
        <WrappedComponent {...props} />
      </ExternalAuthGuard>
    );
  };
}

/**
 * This higher-order component takes a component and wraps it with an InternalAuthGuard
 *
 * @param {ComponentType<P>} WrappedComponent
 * @returns {ComponentType<P>} WrappedComponentWithInternalAuthGuard
 */
function withInternalAuthGuard<P extends object>(
  WrappedComponent: ComponentType<P>,
): ComponentType<P> {
  return function WrappedComponentWithInternalAuthGuard(props: P) {
    return (
      <InternalAuthGuard>
        <WrappedComponent {...props} />
      </InternalAuthGuard>
    );
  };
}
/**
 * This higher-order component takes a function that returns a default export wraps it Suspense and a fallback
 *
 * @param {ComponentType<P>} WrappedComponent
 * @returns {ComponentType<P>} WrappedComponentWithSuspense
 */
function withSuspense(
  importFunction: () => Promise<{ default: () => React.JSX.Element }>,
  fallback: ReactNode = <Content component="p">Loading...</Content>,
): ComponentType {
  const LazyComponent = React.lazy(importFunction);
  return function WrappedComponentWithSuspense() {
    return (
      <Suspense fallback={fallback}>
        <LazyComponent />
      </Suspense>
    );
  };
}
